import CatLoader from '../components/CatLoader';
import { useState, useRef, useEffect } from 'react';
import api from '../api';
import { usePageState } from '../hooks/usePageState';

export default function Redistribution({ embedded=false }) {
  const { saved, save, clear } = usePageState('redistribution');
  const [data, setData]       = useState(saved?.data || null);
  const [loading, setLoading] = useState(false);
  const [error, setError]     = useState('');
  const [drag, setDrag]       = useState(false);
  const [search, setSearch]   = useState(saved?.search || '');
  const [period, setPeriod]   = useState(saved?.period || '');
  const [showPrompt, setShowPrompt] = useState(false);
  const inputRef = useRef();

  useEffect(() => {
    if (data) save({ data, search, period });
  }, [data, search]);

  const processFile = async (file) => {
    if (!file) return;
    // Извлекаем период из имени файла
    const name = file.name || '';
    const match = name.match(/с[_\s](\d{2}-\d{2}-\d{4})[_\s]по[_\s](\d{2}-\d{2}-\d{4})/i);
    if (match) {
      const from = match[1].split('-').reverse().join('.');
      const to = match[2].split('-').reverse().join('.');
      setPeriod(`${from} — ${to}`);
    } else setPeriod('');
    setLoading(true);
    setError('');
    setData(null);
    try {
      const form = new FormData();
      form.append('file', file);
      const { data: result } = await api.post('/redistribution/analyze', form);
      setData(result);
      setShowPrompt(false);
    } catch(e) {
      setError(e.response?.data?.error || 'Ошибка обработки файла');
    } finally {
      setLoading(false);
    }
  };

  const onDrop = (e) => {
    e.preventDefault();
    setDrag(false);
    const file = e.dataTransfer.files[0];
    if (file) processFile(file);
  };

  const handleReset = () => {
    setData(null);
    setSearch('');
    clear();
    setShowPrompt(false);
  };

  const filtered = data?.rows?.filter(r =>
    !search || r.art.toLowerCase().includes(search.toLowerCase())
  ) || [];

  return (
    <>
      <CatLoader loading={loading}/>
      <div style={{ padding: 32 }}>
      <h1 style={{ fontSize: 22, fontWeight: 600, marginBottom: 6 }}>Перераспределение остатков</h1>
      <p style={{ color: 'var(--text-muted)', fontSize: 14, marginBottom: 28 }}>
        {period ? `Период: ${period}` : 'Загрузите файл «Поставки по регионам» из WB Аналитики'}
      </p>

      {/* Промпт восстановления */}
      {!data && saved?.data && !showPrompt && (
        <div style={{
          background: 'rgba(59,130,246,0.1)', border: '1px solid rgba(59,130,246,0.3)',
          borderRadius: 12, padding: '16px 20px', marginBottom: 24,
          display: 'flex', alignItems: 'center', justifyContent: 'space-between'
        }}>
          <div>
            <div style={{ fontWeight: 500, marginBottom: 4 }}>Есть сохранённые результаты</div>
            <div style={{ fontSize: 13, color: 'var(--text-muted)' }}>
              {saved.data.positions} позиций, {saved.data.total} шт к перемещению
            </div>
          </div>
          <div style={{ display: 'flex', gap: 10 }}>
            <button onClick={() => setData(saved.data)} style={{
              background: 'var(--accent)', color: '#fff', padding: '8px 16px', fontSize: 13
            }}>Восстановить</button>
            <button onClick={handleReset} style={{
              background: 'var(--surface)', border: '1px solid var(--border)',
              color: 'var(--text-muted)', padding: '8px 16px', fontSize: 13
            }}>Сбросить</button>
          </div>
        </div>
      )}

      {!data && (
        <div
          onDragOver={e => { e.preventDefault(); setDrag(true); }}
          onDragLeave={() => setDrag(false)}
          onDrop={onDrop}
          onClick={() => inputRef.current.click()}
          style={{
            border: `2px dashed ${drag ? 'var(--accent)' : 'var(--border)'}`,
            borderRadius: 16, padding: '60px 40px',
            textAlign: 'center', cursor: 'pointer',
            background: drag ? 'rgba(59,130,246,0.05)' : 'var(--surface)',
            transition: 'all 0.2s', maxWidth: 600,
          }}
        >
          <div style={{ fontSize: 48, marginBottom: 16 }}>📂</div>
          <div style={{ fontSize: 16, fontWeight: 500, marginBottom: 8 }}>
            {loading ? 'Обрабатываем файл...' : 'Перетащите файл или нажмите для выбора'}
          </div>
          <div style={{ fontSize: 13, color: 'var(--text-muted)' }}>
            Файл Excel (.xlsx) — Аналитика → Поставки по регионам
          </div>
          <input ref={inputRef} type="file" accept=".xlsx"
            style={{ display: 'none' }}
            onChange={e => processFile(e.target.files[0])} />
        </div>
      )}

      {loading && (
        <div style={{ color: 'var(--text-muted)', marginTop: 16 }}>⏳ Анализируем данные...</div>
      )}

      {error && (
        <div style={{
          background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.3)',
          borderRadius: 10, padding: '12px 16px', marginTop: 16, color: 'var(--red)'
        }}>{error}</div>
      )}

      {data && (
        <div>
          <div style={{ display: 'flex', gap: 16, marginBottom: 24, flexWrap: 'wrap' }}>
            <div style={{
              background: 'var(--surface)', borderRadius: 12, padding: '16px 20px',
              border: '1px solid var(--border)'
            }}>
              <div style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 4 }}>Всего к перемещению</div>
              <div style={{ fontSize: 24, fontWeight: 700, color: 'var(--accent)' }}>{data.total} шт</div>
            </div>
            <div style={{
              background: 'var(--surface)', borderRadius: 12, padding: '16px 20px',
              border: '1px solid var(--border)'
            }}>
              <div style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 4 }}>Позиций</div>
              <div style={{ fontSize: 24, fontWeight: 700 }}>{data.positions}</div>
            </div>
            {Object.entries(data.summary).map(([region, s]) => (
              (s.from > 0 || s.to > 0) && (
                <div key={region} style={{
                  background: 'var(--surface)', borderRadius: 12, padding: '16px 20px',
                  border: '1px solid var(--border)', minWidth: 160
                }}>
                  <div style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 8 }}>{region}</div>
                  {s.from > 0 && <div style={{ fontSize: 13, color: 'var(--red)' }}>📤 Отправить: {s.from} шт</div>}
                  {s.to > 0 && <div style={{ fontSize: 13, color: 'var(--green)' }}>📥 Получить: {s.to} шт</div>}
                </div>
              )
            ))}
          </div>

          <div style={{ display: 'flex', gap: 12, marginBottom: 16, alignItems: 'center' }}>
            <input placeholder="Поиск по артикулу..." value={search}
              onChange={e => setSearch(e.target.value)} style={{ width: 260 }} />
            <button onClick={handleReset} style={{
              background: 'var(--surface)', border: '1px solid var(--border)',
              color: 'var(--text-muted)', padding: '8px 16px', fontSize: 13
            }}>↩ Загрузить другой файл</button>
            <div style={{ color: 'var(--text-muted)', fontSize: 13 }}>
              Показано: {filtered.length} из {data.rows.length}
            </div>
          </div>

          <div style={{ overflowX: 'auto', borderRadius: 12, border: '1px solid var(--border)' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
              <thead>
                <tr style={{ background: 'var(--surface)' }}>
                  {['Артикул','Арт.WB','Размер','Откуда (регион)','Откуда (склад)','Остаток','Заказов','Куда (регион)','Куда (склад)','Остаток','Заказов','Переместить'].map((h, i) => (
                    <th key={i} style={{
                      padding: '10px 12px', textAlign: 'left',
                      color: 'var(--text-muted)', fontWeight: 500,
                      borderBottom: '1px solid var(--border)', whiteSpace: 'nowrap'
                    }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {filtered.map((r, i) => (
                  <tr key={i} style={{
                    borderBottom: '1px solid var(--border)',
                    background: i % 2 === 0 ? 'transparent' : 'rgba(255,255,255,0.02)'
                  }}>
                    <td style={{ padding: '9px 12px', fontWeight: 500 }}>{r.art}</td>
                    <td style={{ padding: '9px 12px', color: 'var(--text-muted)' }}><span className="blur-nmid">{r.nmId}</span></td>
                    <td style={{ padding: '9px 12px' }}>{r.size}</td>
                    <td style={{ padding: '9px 12px', color: 'var(--red)' }}>{r.fromRegion}</td>
                    <td style={{ padding: '9px 12px', color: 'var(--text-muted)', fontSize: 12 }}>{r.fromWarehouse}</td>
                    <td style={{ padding: '9px 12px' }}>{r.fromStock}</td>
                    <td style={{ padding: '9px 12px' }}>{r.fromOrders}</td>
                    <td style={{ padding: '9px 12px', color: 'var(--green)' }}>{r.toRegion}</td>
                    <td style={{ padding: '9px 12px', color: 'var(--text-muted)', fontSize: 12 }}>{r.toWarehouse}</td>
                    <td style={{ padding: '9px 12px' }}>{r.toStock}</td>
                    <td style={{ padding: '9px 12px' }}>{r.toOrders}</td>
                    <td style={{ padding: '9px 12px' }}>
                      <span style={{
                        background: 'rgba(59,130,246,0.15)', color: 'var(--accent)',
                        padding: '3px 10px', borderRadius: 6, fontWeight: 600
                      }}>{r.qty}</span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
    </>
  );
}